// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import CookiePrivacyPolicyPopup from "@insite/content-library/Components/CookiePrivacyPolicyPopup";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getIsCheckoutPage } from "@plumberssupply/App/Store/Selectors/GeneralSelectors";
import GridContainer, { GridContainerProps } from "@plumberssupply/MobiusOverrides/GridContainer/GridContainer";
import { constants } from "@plumberssupply/XDL/constants";
import { color, layoutContainer, xScreen } from "@plumberssupply/XDL/Utilities";
import * as React from "react";
import { css } from "styled-components";

export interface FooterStyles {
    page?: MobiusPageProps;
    subscribtionWrapper?: InjectableCss;
    footerWrapper?: InjectableCss;
    checkoutFooterWrapper?: InjectableCss;
    subscribtionGridContainer?: InjectableCss;
    subscribtionGridItem?: InjectableCss;
    footerGridContainer?: InjectableCss;
    subscribtion?: InjectableCss;
    logoGridItem?: InjectableCss;
    logoWrapper?: InjectableCss;
    logo?: InjectableCss;
    footerGridItem?: InjectableCss;
}

export const footerStyles: FooterStyles = {
    page: {
        fullWidth: [true, true, true, true, true],
        css: css`
            margin-top: ${constants.footer.offsetTop}px;
            ${xScreen("lte", "md")} {
                overflow: visible;
            }
        `,
    },
    subscribtionWrapper: {
        css: css`
            background-color: ${color("common.ui11")};
            position: relative;
            overflow-x: clip;
        `,
    },
    footerWrapper: {
        css: css`
            background-color: ${color("primary")};
        `,
    },
    checkoutFooterWrapper: {
        css: css`
            background-color: ${color("text")};
        `,
    },
    subscribtionGridContainer: {
        css: css`
            margin: 0;
            display: flex;
            gap: 15px;
            ${layoutContainer}
        `,
    },
    footerGridContainer: {
        css: css`
            display: flex;
            ${layoutContainer}
        `,
    },
    subscribtionGridItem: {
        css: css`
            padding: 24px 42px 24px 0;
            width: calc(200% / 3);
            ${xScreen("lte", "md")} {
                width: 100%;
                padding: 24px 0;
            }
        `,
    },
    logoGridItem: {
        css: css`
            padding: 0;
            position: relative;
            &&& {
                margin-top: 15px;
            }
            width: calc(100% / 3);
            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
    logoWrapper: {
        css: css`
            background-color: ${color("primary")};
            width: calc(100% + 1000px);
            height: 100%;
            position: absolute;
            bottom: 0;
            overflow: hidden;
            &:after {
                content: "";
                position: absolute;
                margin: -21px -19px;
                width: 40px;
                height: 45px;
                transform: rotate(45deg);
                background-color: ${color("common.ui11")};
            }
        `,
    },
    logo: {
        css: css`
            padding-bottom: 10px;
            position: absolute;
            top: 24px;
            left: 32px;
            a {
                width: 280px;
                height: 64px;
            }
            img {
                max-height: 100%;
            }
        `,
    },
    footerGridItem: {
        css: css`
            width: 100%;
        `,
    },
};

const styles = footerStyles;
const Footer = ({ id }: PageProps) => {
    const { isCheckoutPage } = useAppSelector(state => ({
        isCheckoutPage: getIsCheckoutPage(state),
    }));

    return (
        <Page as="footer" isFooter={true} {...styles.page}>
            {!isCheckoutPage && (
                <StyledWrapper {...styles.subscribtionWrapper}>
                    <StyledWrapper {...styles.subscribtionGridContainer}>
                        <StyledWrapper {...styles.subscribtionGridItem}>
                            <Zone {...styles.subscribtion} contentId={id} zoneName="Subscribtion" />
                        </StyledWrapper>
                        <StyledWrapper {...styles.logoGridItem}>
                            <StyledWrapper {...styles.logoWrapper}>
                                <StyledWrapper {...styles.logo}>
                                    <Zone contentId={id} zoneName="FooterLogo" />
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            )}
            <StyledWrapper {...(isCheckoutPage ? styles.checkoutFooterWrapper : styles.footerWrapper)}>
                <StyledWrapper {...styles.footerGridContainer}>
                    <StyledWrapper {...styles.footerGridItem}>
                        <Zone contentId={id} zoneName="FooterContainer" />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <CookiePrivacyPolicyPopup />
        </Page>
    );
};

const pageModule: PageModule = {
    component: Footer,
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "Footer" instead of this constant.
 */
export const FooterContext = "Footer";
